// Single source of truth for ordering the merged list of built-in
// + custom fields on the customer-facing request form. Both the
// hosted /request page AND the embed bundle import this so a drag
// in /settings/forms reflows both surfaces identically.
//
// Pure helper — no React, no DOM, no server deps — so it can run in
// the embed bundle too.
import type {
  BuiltinFieldKey,
  BuiltinFieldConfig,
  CustomField,
  ShowWhenRule,
  FormStepsConfig,
} from "@/lib/shop-config-types";
import {
  BUILTIN_FIELD_ROWS,
  resolveBuiltinStep,
  resolveBuiltinOrder,
  resolveBuiltinColumnWidth,
} from "@/lib/builtin-fields";

export type FormStepId = "about" | "shipping" | "finish";

// Total order over steps — the form renders "about" then "shipping"
// then "finish". Used as the primary sort key in buildOrderedFields;
// a plain `a.step !== b.step` inequality check is NOT a valid
// comparator (it's non-transitive: it reports every pair of differing
// steps as "equal", which sort() is free to resolve however it likes).
const STEP_RANK: Record<FormStepId, number> = { about: 0, shipping: 1, finish: 2 };

export type OrderedField =
  | { kind: "builtin"; key: BuiltinFieldKey; step: FormStepId; order: number; column_width: "full" | "half" | "third"; show_when?: ShowWhenRule }
  | { kind: "custom"; field: CustomField; step: FormStepId; order: number; column_width: "full" | "half" | "third"; show_when?: ShowWhenRule };

interface OrderConfig {
  builtin_fields?: Partial<Record<BuiltinFieldKey, BuiltinFieldConfig>>;
  custom_fields?: CustomField[];
  require_shipping?: boolean;
  form_steps?: FormStepsConfig;
}

/** Walk every built-in row + every custom field, resolve the
 *  effective step + display_order, drop shipping fields when the
 *  section toggle is off, and return the merged list sorted by
 *  (step, order). Caller picks `step === currentStepId`. */
export function buildOrderedFields(config: OrderConfig | undefined): OrderedField[] {
  // Shipping enabled: new form_steps.shipping.enabled wins over legacy
  // require_shipping. Inlined here (instead of importing form-steps.ts)
  // so this module stays import-free of types that aren't already in
  // its tree — keeps the dep graph shallow for the embed-bundle parallel
  // copy that mirrors this logic.
  const formStepsShip = config?.form_steps?.shipping?.enabled;
  const requireShipping = typeof formStepsShip === "boolean"
    ? formStepsShip
    : config?.require_shipping !== false;
  const out: OrderedField[] = [];

  for (const row of BUILTIN_FIELD_ROWS) {
    const step = resolveBuiltinStep(row.key, config);
    if (step === "shipping" && !requireShipping) continue;
    const ov = config?.builtin_fields?.[row.key];
    out.push({
      kind: "builtin",
      key: row.key,
      step,
      order: resolveBuiltinOrder(row.key, config),
      column_width: resolveBuiltinColumnWidth(row.key, config),
      show_when: ov?.show_when,
    });
  }

  const customs = config?.custom_fields || [];
  customs.forEach((f, i) => {
    out.push({
      kind: "custom",
      field: f,
      step: f.step,
      order: typeof f.display_order === "number" ? f.display_order : 100 + i,
      column_width: f.column_width === "half" ? "half" : f.column_width === "third" ? "third" : "full",
      show_when: f.show_when,
    });
  });

  // Stable sort: rank by step first (a total order, not just an
  // inequality check — `return 0` for differing steps is non-transitive
  // and lets Array.prototype.sort produce an arbitrary permutation),
  // then by order ascending within a step. We keep input order as the
  // tie-breaker so identical orders fall back to the merchant's
  // original sequence (stable sort does this for free).
  out.sort((a, b) => {
    const stepDelta = STEP_RANK[a.step] - STEP_RANK[b.step];
    if (stepDelta !== 0) return stepDelta;
    return a.order - b.order;
  });
  return out;
}

/** Convenience: just the rows that render on the given step. */
export function orderedFieldsForStep(
  config: OrderConfig | undefined,
  step: FormStepId,
): OrderedField[] {
  return buildOrderedFields(config).filter(f => f.step === step);
}

/** Stable identifier for a field — used as the key into formValues and
 *  as the dependency target for show_when rules. Built-ins use their
 *  BuiltinFieldKey verbatim ("phone", "email"); customs use their
 *  CustomField.id. */
export function fieldDependencyId(row: OrderedField): string {
  return row.kind === "builtin" ? row.key : row.field.id;
}

/** Stable identifier for a field row — like fieldDependencyId but
 *  namespaced by kind, matching the `sortId` dnd-kit needs in
 *  /settings/forms. Exported here (not reimplemented in the designer)
 *  so the designer's row list and the renderer's ordered list are keyed
 *  identically and a future edit can't quietly fork the two. */
export function fieldSortId(row: OrderedField): string {
  return row.kind === "builtin" ? `builtin:${row.key}` : `custom:${row.field.id}`;
}

/** Pretty-print a value coming out of formValues (string OR string[])
 *  for the matcher. Multiselects compare element-wise. */
function depValueMatches(
  dep: string | string[] | undefined,
  target: string | string[],
  mode: "equals" | "contains" | "intersect",
): boolean {
  const targetArr = Array.isArray(target) ? target : [target];
  const depArr = Array.isArray(dep) ? dep : (typeof dep === "string" ? [dep] : []);
  if (mode === "equals") {
    // For scalar dep, exact match; for array dep, treat "equals X" as
    // "the array contains X" so the rule stays meaningful against
    // multiselect dependencies (matches the spec).
    if (typeof target !== "string") return false;
    if (Array.isArray(dep)) return dep.includes(target);
    return (dep || "") === target;
  }
  if (mode === "contains") {
    if (typeof target !== "string") return false;
    if (Array.isArray(dep)) return !dep.includes(target);
    return (dep || "") !== target;
  }
  // intersect — any overlap between dep value(s) and target list.
  return targetArr.some(t => depArr.includes(t));
}

/** Evaluate a field's show_when rule against the current form state.
 *  Returns true when:
 *   - the field has no rule, OR
 *   - the dep's value satisfies the operator/value combination.
 *  formValues is a flat map keyed by fieldDependencyId() — callers
 *  build it from their built-in state slots (e.g. `phone: state.phone`)
 *  merged with the customValues blob. */
export function isFieldVisible(
  field: OrderedField,
  formValues: Record<string, string | string[] | undefined>,
): boolean {
  const rule = getShowWhen(field);
  if (!rule) return true;
  const dep = formValues[rule.field_id];
  switch (rule.operator) {
    case "equals":
      return depValueMatches(dep, rule.value as string, "equals");
    case "not_equals":
      return depValueMatches(dep, rule.value as string, "contains");
    case "in":
      return depValueMatches(dep, Array.isArray(rule.value) ? rule.value : [rule.value], "intersect");
    case "not_in": {
      const inHit = depValueMatches(dep, Array.isArray(rule.value) ? rule.value : [rule.value], "intersect");
      return !inHit;
    }
  }
}

function getShowWhen(row: OrderedField): ShowWhenRule | undefined {
  return row.show_when;
}

